import { useCallback, useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { EventType, Rive } from '@rive-app/react-webgl2';
import {
  atomCharacterChargeState,
  atomCharacterDropQueue,
  CharacterAnimation,
} from '../states/game.atoms';
import { useSound } from '../components/SoundHandler';

// Constants
const BUFFER_DELAY_MS = 200;
const PUKE_DRIP_INTERVAL_FRAMES = 2; // frames between each ball drop during puke

type AnimationTriggers = {
  [K in CharacterAnimation]?: () => void;
};

interface UseCharacterAnimationProps {
  rive: Rive | null;
  triggers: AnimationTriggers;
}

export const useCharacterAnimation = ({
  rive,
  triggers,
}: UseCharacterAnimationProps) => {
  const sound = useSound()

  // Atom states
  const chargeState = useAtomValue(atomCharacterChargeState);
  const [dropQueue, setDropQueue] = useAtom(atomCharacterDropQueue);

  // Refs
  const incomingQueueRef = useRef<Array<() => void>>([]);
  const pukeDripQueueRef = useRef<Array<() => void>>([]);
  const bufferTimerRef = useRef<number | null>(null);
  const inDropWindowRef = useRef<boolean>(false);
  const chargeInReadyPoseRef = useRef<boolean>(false);
  const pukeDripFrameCounterRef = useRef<number>(0);
  const currentAnimationRef = useRef<CharacterAnimation>('idle');
  const prevChargeStateRef = useRef(chargeState);
  const triggersRef = useRef(triggers);
  triggersRef.current = triggers;

  // Play animation helper
  const playAnimation = useCallback((animation: CharacterAnimation) => {
    currentAnimationRef.current = animation;
    triggersRef.current[animation]?.();
  }, []);

  // Handle dropstart event
  const handleDropStart = useCallback(() => {
    inDropWindowRef.current = true;
    const current = currentAnimationRef.current;

    if (current === 'puke-single') {
      const cb = incomingQueueRef.current.shift();
      if (cb) {
        sound.play('characterPukeSingle');
        cb();
      }
    } else if (current === 'puke') {
      // Move all incoming to pukeDrip queue
      while (incomingQueueRef.current.length) {
        const cb = incomingQueueRef.current.shift();
        pukeDripQueueRef.current.push(cb!);
      }
      // Drop the first one immediately
      const first = pukeDripQueueRef.current.shift();
      if (first) {
        sound.play('characterPuke');
        first();
      }
      pukeDripFrameCounterRef.current = 0;
    }
  }, [sound]);

  // Handle dropend event
  const handleDropEnd = useCallback(() => {
    inDropWindowRef.current = false;
  }, []);

  // Handle animation end
  const handleAnimationEnd = useCallback(() => {
    const current = currentAnimationRef.current;

    if (current === 'puke-start') {
      if (chargeInReadyPoseRef.current) {
        playAnimation('puke-hold');
      } else {
        playAnimation('puke');
      }
      return;
    }

    if (current === 'puke') {
      if (pukeDripQueueRef.current.length > 0 || incomingQueueRef.current.length > 0) {
        playAnimation('puke');
      } else {
        playAnimation('puke-end');
      }
      return;
    }

    if (current === 'puke-single') {
      const q = incomingQueueRef.current.length;
      if (q >= 2) {
        playAnimation('puke-start');
      } else if (q >= 1) {
        playAnimation('puke-single');
      } else {
        playAnimation('idle');
      }
      return;
    }

    // puke-end or any other -> idle
    playAnimation('idle');
  }, [playAnimation]);

  // Process puke drip queue during puke animation (frame-based interval)
  useEffect(() => {
    const interval = setInterval(() => {
      if (currentAnimationRef.current !== 'puke' || !inDropWindowRef.current) {
        pukeDripFrameCounterRef.current = 0;
        return;
      }
      if (pukeDripQueueRef.current.length > 0) {
        pukeDripFrameCounterRef.current += 1;
        if (pukeDripFrameCounterRef.current >= PUKE_DRIP_INTERVAL_FRAMES) {
          const cb = pukeDripQueueRef.current.shift();
          if (cb) {
            sound.play('characterPuke');
            cb();
          }
          pukeDripFrameCounterRef.current = 0;
        }
      }
    }, 16); // ~60fps

    return () => clearInterval(interval);
  }, [sound]);

  // Handle incoming drop queue from atoms
  useEffect(() => {
    if (dropQueue.length === 0) return;

    const current = currentAnimationRef.current;

    for (const dropCallback of dropQueue) {
      // If in ready pose, exit it
      if (chargeInReadyPoseRef.current) {
        chargeInReadyPoseRef.current = false;
      }

      // If currently in puke window, enqueue for drip
      if (current === 'puke' && inDropWindowRef.current) {
        pukeDripQueueRef.current.push(dropCallback);
        continue;
      }

      // Otherwise, queue to incoming
      incomingQueueRef.current.push(dropCallback);
    }

    // Clear atom queue
    setDropQueue([]);

    // If idle or not in puke sequence, start buffer to decide animation
    const isPlaying = current !== 'idle';
    if ((!isPlaying || (current !== 'puke' && current !== 'puke-start')) && bufferTimerRef.current === null) {
      bufferTimerRef.current = window.setTimeout(() => {
        bufferTimerRef.current = null;
        const count = incomingQueueRef.current.length;
        if (count >= 2) {
          playAnimation('puke-start');
        } else if (count >= 1) {
          playAnimation('puke-single');
        }
      }, BUFFER_DELAY_MS);
    }
  }, [dropQueue, setDropQueue, playAnimation]);

  // Handle charge state changes
  useEffect(() => {
    const current = currentAnimationRef.current;
    const previousState = prevChargeStateRef.current;
    prevChargeStateRef.current = chargeState;

    if (chargeState === 'idle') {
      // If previously ready, don't force idle (let animation finish)
      if (previousState === 'ready') {
        return;
      }

      chargeInReadyPoseRef.current = false;

      // Do not override active drop/puke sequences
      if (current === 'puke-single' || current === 'puke') {
        return;
      }

      if (current === 'puke-start' || current === 'puke-hold') {
        if (incomingQueueRef.current.length === 0 && pukeDripQueueRef.current.length === 0 && !inDropWindowRef.current) {
          playAnimation('idle');
        }
        return;
      }

      if (current !== 'idle') {
        playAnimation('idle');
      }
      return;
    }

    if (chargeState === 'charging') {
      chargeInReadyPoseRef.current = false;

      if (current && current !== 'idle' && current !== 'puke-start' && current !== 'puke-hold') {
        return;
      }

      if (current !== 'idle' && current !== 'puke-start') {
        playAnimation('idle');
      }
      return;
    }

    if (chargeState === 'ready') {
      chargeInReadyPoseRef.current = true;
      if (bufferTimerRef.current !== null) {
        window.clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = null;
      }
      if (current !== 'puke-hold') {
        playAnimation('puke-hold');
      }
      return;
    }
  }, [chargeState, playAnimation]);

  // Listen to Rive events
  useEffect(() => {
    if (!rive) return;

    const onRiveEvent = (riveEvent: any) => {
      const eventName = riveEvent.data?.name;
      if (eventName === 'character-puke-start') {
        handleDropStart();
      } else if (eventName === 'character-puke-end') {
        handleDropEnd();
      } else if (eventName === 'character-puke-animation-ended') {
        handleAnimationEnd();
      }
    };

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
    };
  }, [rive, handleDropStart, handleDropEnd, handleAnimationEnd]);
};
